"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Droplets, Flame, Wrench, CloudRain, AlertTriangle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  id: "blocked-drains",
  title: "Blocked Drains",
  heading: "Have a Blocked Drain?",
  description: "We utilize high-definition CCTV and hydro-jetting to clear obstructions instantly, restoring flow and protecting your plumbing infrastructure with modern technical precision.",
  benefits: ["Drain camera inspections", "Hydro-jetting technology", "Strategic root intrusion removal", "Ongoing flow maintenance"],
  image: "https://true-pony.10web.cloud/wp-content/uploads/2026/03/Blocked-Drain.jpg?v=axkikelkmed",
  icon: Droplets,
  link: "/ServicesPage"
}, {
  id: "hot-water",
  title: "Hot Water Systems",
  heading: "Premium Hot Water System Upgrades",
  description: "Experience consistent comfort with our high-efficiency hot water installations and rapid repair services. We specialize in modern, energy-saving systems across the Central Coast.",
  benefits: ["Same-day emergency repairs", "Local response team (Narara)", "Gas, electric & heat pump experts", "Professional system disposal"],
  image: "https://cliffhangerplumbing.com.au/wp-content/uploads/2026/03/Hot-Water-System-scaled.jpg?v=9f32w4oycak",
  icon: Flame,
  link: "/ServicesPage"
}, {
  id: "gas-fitting",
  title: "Gas Fitting",
  heading: "Professional Gas Infrastructure",
  description: "Our certified technicians deliver precision gas fitting, from complex appliance integration to comprehensive safety audits, ensuring Narara homes remain secure and compliant.",
  benefits: ["Electronic leak detection", "New gas line architecture", "Appliance commissioning", "Safety compliance certificates"],
  image: "https://true-pony.10web.cloud/wp-content/uploads/2026/03/Gasfitting.jpg?v=t6v67404jyp",
  icon: Wrench,
  link: "/ServicesPage"
}, {
  id: "stormwater",
  title: "Stormwater",
  heading: "Advanced Stormwater Systems",
  description: "Safeguard your landscape with engineered drainage solutions designed to handle the Central Coast's heaviest rainfall events while preventing structural water damage.",
  benefits: ["Custom drainage architecture", "Main line clearing & repair", "Gutter & downpipe optimization", "Sub-soil drainage solutions"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581682/img/service_stormwater_detail.png",
  icon: CloudRain,
  link: "/ServicesPage"
}, {
  id: "emergency",
  title: "Emergency Plumbing",
  heading: "Round-the-Clock Emergency Care",
  description: "Plumbing disasters don't wait for business hours. Our dedicated rapid-response team is stationed locally in Narara, ready to resolve critical plumbing failures at any hour.",
  benefits: ["24/7 priority dispatch", "Local Narara response team", "Burst pipe containment", "Sewage overflow mitigation"],
  image: "https://true-pony.10web.cloud/wp-content/uploads/2026/03/Emergency-Water-Repair.jpg?v=aqrt0zw829a",
  icon: AlertTriangle,
  link: "/ServicesPage"
}];
export default function ExpertiseAtAGlance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="108962" id="our-specialized-expertise" className="py-24 bg-background text-foreground overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-primary">Our Expertise at a Glance</h2>
          <p className="text-lg md:text-xl text-muted-foreground font-medium">
            Discover our comprehensive range of premium plumbing services, delivered with precision and care across Narara and the Central Coast.
          </p>
        </div>

        <Tabs defaultValue={servicesData[0].id} className="w-full max-w-6xl mx-auto">
          <div className="flex justify-start md:justify-center mb-12 md:mb-20 overflow-x-auto md:overflow-visible pb-8 w-full hide-scrollbar">
            <TabsList className="h-auto p-2 bg-secondary/50 rounded-full flex flex-nowrap justify-start md:justify-between gap-2 w-full">
              {servicesData.map((service, i) => <TabsTrigger key={service.id} value={service.id} data-index={i} className="group relative rounded-full px-6 py-3.5 text-sm md:text-base font-bold flex items-center transition-all duration-500 whitespace-nowrap data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-primary data-[state=active]:bg-accent data-[state=active]:text-primary data-[state=active]:shadow-sm">
                  <service.icon className="w-5 h-5 mr-2 transition-transform duration-300 group-data-[state=active]:scale-110" />
                  {service.title}
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-1 bg-primary rounded-full transition-all duration-500 group-data-[state=active]:w-1/2" />
                </TabsTrigger>)}
            </TabsList>
          </div>

          {servicesData.map((service, i) => {
          const IconComponent = service.icon;
          return <TabsContent key={service.id} value={service.id} data-index={i} className="mt-0 focus-visible:outline-none data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=active]:fade-in-0 data-[state=inactive]:fade-out-0 data-[state=active]:slide-in-from-bottom-4 data-[state=inactive]:slide-out-to-bottom-4 duration-500 ease-in-out">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                  
                  <div className="relative order-2 lg:order-1 px-4 sm:px-8 lg:px-0">
                    <div className="aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/3] rounded-[40px] overflow-hidden shadow-2xl relative group">
                      <img src={service.image} alt={service.heading} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent mix-blend-overlay pointer-events-none"></div>
                    </div>
                    
                    <div className="absolute -bottom-6 -right-2 sm:-bottom-8 sm:-right-4 lg:-bottom-10 lg:-right-10 bg-background p-3 sm:p-4 lg:p-6 rounded-[24px] shadow-2xl border border-border/50 z-10 animate-in zoom-in duration-700 delay-300">
                      <div className="bg-accent p-4 sm:p-5 rounded-2xl text-accent-foreground flex items-center justify-center">
                        <IconComponent className="w-8 h-8 sm:w-10 sm:h-10 lg:w-14 lg:h-14" strokeWidth={1.2} />
                      </div>
                    </div>
                  </div>

                  <div className="order-1 lg:order-2 space-y-8 lg:space-y-10">
                    <div className="space-y-6">
                      <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.1] tracking-tight">
                        {service.heading}
                      </h3>
                      <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed font-normal">
                        {service.description}
                      </p>
                    </div>
                    
                    <ul className="space-y-5 pt-2">
                      {service.benefits.map((benefit, j) => <li key={j} data-index={j} className="flex items-start gap-3 sm:gap-5">
                          <div className="mt-1.5 bg-accent/50 rounded-full p-1 shrink-0">
                            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-accent-foreground" />
                          </div>
                          <span className="text-foreground font-semibold text-lg sm:text-xl tracking-tight">
                            {benefit}
                          </span>
                        </li>)}
                    </ul>
                    
                    <div className="pt-4 sm:pt-8">
                      <Button asChild size="lg" className="rounded-full px-10 h-14 sm:h-16 text-lg sm:text-xl font-bold bg-primary text-primary-foreground shadow-xl shadow-primary/20 hover:shadow-2xl hover:shadow-primary/30 transition-all duration-300 hover:-translate-y-1">
                        <Link to={service.link}>Learn More</Link>
                      </Button>
                    </div>
                  </div>

                </div>
              </TabsContent>;
        })}
        </Tabs>
      </div>
    </section>;
}